<template>
  <div
    class="file-item"
    :class="{
      'file-item_editable': isEditable,
      'file-item-download': !advancedMode,
      'file-item-dragging-valid': isDragValid && isDragStart,
      'file-item-dragging-invalid': !isDragValid && isDragStart,
      'file-item--flash': isFlashing,
      'file-item--multiline': isMultiline,
    }"
    ref="fileItemRef"
    :style="levelStyle"
    @mousedown.capture="handleMouseDownCapture"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave($event)"
    :draggable="
      ((props.ruleAccess === 2 && file.Editable) || isAdmin) &&
      advancedMode &&
      isUploading == false &&
      dragGeneralState.isValid &&
      file.isSelected == true
        ? true
        : false
    "
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @dragover="handleDragOver"
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
    @drag="onDrag"
    @click="handleFileItemClick"
  >
    <app-checkbox
      class="file-item__checkbox"
      v-if="advancedMode && fileType == 'default' && !isUploading"
      :value="file.isSelected"
      @input="selectFile"
      @click.stop
      :disabled="
        !isAdmin &&
        (props.ruleAccess === 0 ||
          !file.Editable)
      "
    />
    <app-icon
      class="file-item__icon"
      :name="dragIconName"
      height="18px"
      width="18px"
      :color="isUploading == false ? '#979797' : '#d2d2d2'"
      cursor="pointer"
    />
    <div class="file-item__string">
      <template v-if="fileType == 'default'">
        <app-hashtag
          v-if="(props.ruleAccess === 2 && file.Editable) || isAdmin"
          ref="hashtagRef"
          class="file-item__string-hashtag"
          :class="{
            'file-item__string-hashtag-no-empty': file.Tags.length !== 0,
          }"
          :items="file.Tags"
          :show-clear="true"
          :tooltip-text="$t('file_add_tag')"
          :disabled="(props.ruleAccess < 2 && !isAdmin) || isUploading == true"
          :selector-enabled="true"
          :selector-value="selectedTags"
          :entity-type="TagEntityType.File"
          :entity-id="file.Id"
          :tag-selector-mode="'select'"
          @update:selectorValue="changeTags"
          @create-tag="onFileTagCreated"
          @remove="removeTag"
        />
        <app-hashtag
          v-else
          ref="hashtagRef"
          class="file-item__string-hashtag"
          :class="{
            'file-item__string-hashtag-no-empty': file.Tags.length !== 0,
          }"
          :items="file.Tags"
          :show-clear="true"
          :selector-enabled="true"
          :selector-value="selectedTags"
          :entity-type="TagEntityType.File"
          :entity-id="file.Id"
          type="text"
        />
      </template>
      <div class="file-item__name">
        <span
          v-show="!isNameEditable && searchMode"
          :class="{
            'file-item__name-disable':
              (!isAdmin && !file.Editable) || isUploading == true,
          }"
          v-html="highlightedName"
        ></span>
        <file-input
          v-show="isNameEditable || !searchMode"
          type="file"
          :model-value="file.Name"
          :invalid-values="siblingFileNames"
          ref="inputRef"
          @enter="renameFile"
          @blur="blurInput"
          :disabled="
            (!isAdmin && (props.ruleAccess == 0 || !file.Editable)) ||
            isUploading == true
          "
          :blocked="!isAdmin && props.ruleAccess !== 2 && !file.Editable"
        />
      </div>
      <div v-if="isUploading" class="file-item__percent">
        <app-icon
          style="margin-left: 4px; margin-top: -4px"
          name="clock-upload"
          width="12px"
          color="#d2d2d2"
          hover-color="#499259"
          cursor="pointer"
        />
        <div class="file-item__percent-text">{{ percentUploading }}%</div>
      </div>
      <app-icon
        style="margin-left: 4px; margin-top: -4px"
        v-if="isUploading"
        name="close"
        width="12px"
        color="#d2d2d2"
        hover-color="#499259"
        cursor="pointer"
        @click="abortUpload"
      />
    </div>
    <file-resizable-dropdown
      v-if="
        showJobIcon &&
        file.JobBindings.length > 1 &&
        !(!isEditable || isNameEditable)
      "
      ref="jobDropdown"
      position="right"
      :resizable="false"
      trigger="hover"
      :hover-delay="300"
      :hover-close-delay="300"
      :close-by-click-outside="false"
      :max-width="320"
      :max-height="236"
      @open="loadBoundJobs"
    >
      <template #activator>
        <app-tooltip :text="$t('file_go_to_job')">
          <template #activator>
            <app-icon
              class="file-item__option-icons-icon"
              name="go-job"
              width="16px"
              color="#979797"
              cursor="pointer"
              hover-color="#499259"
            />
          </template>
        </app-tooltip>
      </template>
      <template #default>
        <div class="file-item__jobs-list">
          <template v-for="(job, index) in boundJobs" :key="job.Job.Id">
            <div v-if="index !== 0" class="file-item__jobs-list-divider" />
            <file-job-element :job="job.Job" @select-job="goToJob" />
          </template>
        </div>
      </template>
    </file-resizable-dropdown>
    <app-tooltip
      v-else-if="showJobIcon && !(!isEditable || isNameEditable)"
      :text="$t('file_go_to_job')"
    >
      <template #activator>
        <app-icon
          class="file-item__option-icons-icon"
          name="go-job"
          width="16px"
          color="#979797"
          cursor="pointer"
          hover-color="#499259"
          @click.stop="goToSingleJob"
        />
      </template>
    </app-tooltip>
    <div
      v-if="fileType == 'default' && !isUploading"
      ref="menuAnchorRef"
      class="file-item__option-icons"
      :class="{
        'file-item__option-icons-icon-invisible': !isEditable || isNameEditable,
      }"
    >
      <app-tooltip
        :text="$t('file_edit_name')"
        v-if="isAdmin || (props.ruleAccess === 2 && file.Editable)"
        ><template #activator>
          <app-icon
            class="file-item__option-icons-icon"
            name="edit-pencil"
            width="16px"
            color="#979797"
            cursor="pointer"
            hover-color="#499259"
            @click.stop="editName"
          />
        </template>
      </app-tooltip>
      <file-dropdown
        v-if="file.Editable"
        ref="dropdownMenu"
        type="menu"
        @click.stop
      >
        <file-menu
          :elements="menuElementsFile"
          @select="
            (option) => {
              setModalPositionNearDropdown();
              chooseAction(option);
            }
          "
          type="file"
          :-job-bindings="props.file.JobBindings"
          @jobs-closed="onJobsClosed"
          @job-selected="resetJobNavigationState"
        />
      </file-dropdown>
      <app-tooltip :text="$t('file_view_information')" v-else>
        <template #activator>
          <app-icon
            class="folder__option-icons-update"
            name="info-circle-outline"
            width="16px"
            color="#979797"
            cursor="pointer"
            hover-color="#499259"
            @mousedown.prevent
            @click.stop="chooseAction('infoFile')"
          />
        </template>
      </app-tooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  inject,
  Ref,
  nextTick,
  watch,
  onMounted,
  onUnmounted,
} from "vue";
import AppHashtag from "@/components/ui-kit/hashtag/AppHashtag.vue";
import FileInput from "./FileInput.vue";
import {
  DragGeneralState,
  FileOut,
  FolderOut,
} from "@/models/interfaces/file/file-model";
import AppIcon from "../ui-kit/icon/AppIcon.vue";
import { typeIconMap, getFileTypeFromString } from "@/utils/fileIcons";
import AppTooltip from "../ui-kit/tooltip/AppTooltip.vue";
import AppCheckbox from "../ui-kit/checkbox/AppCheckbox.vue";
import FileMenu from "./FileMenu.vue";
import FileDropdown from "./FileDropdown.vue";
import FileResizableDropdown from "./fileUi/FileResizableDropdown.vue";
import FileJobElement from "./FileJobElement.vue";
import { navigateToJob } from "./jobNavigation";
import { JobsApi } from "@/ api/JobsApi";
import { Job, JobInfo } from "@/store/jobs";
import { clientPageStore } from "@/store/client-page";
import { projectPageStore } from "@/store/project-page";
import { useRoute, useRouter } from "vue-router";
import { ProjectContainer } from "@/models/interfaces/project/project-models";
import { TagBaseType, TagEntityType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { escapeHtml, highlightFileSearchText } from "@/utils/textHelpers";
import { i18n } from "@/i18n";
import { useBreakpoints } from "@/composables/useBreakpoints";

interface Props {
  file: FileOut;
  level?: number;
  parentNode?: FolderOut;
  fileType?: "default";
  isUploading?: boolean;
  ruleAccess: number;
}

const props = withDefaults(defineProps<Props>(), {
  level: 0,
  fileType: "default",
  isUploading: false,
  ruleAccess: 0,
});

const { isMobile } = useBreakpoints();

const siblingFileNames = computed(() =>
  (props.parentNode?.Files ?? [])
    .filter((item) => item.Id !== props.file.Id)
    .map((item) => item.Name),
);

const clientStore = clientPageStore();
const projectStore = projectPageStore();
const route = useRoute();
const router = useRouter();
const boundJobs = ref<JobInfo[]>([]);
const boundJobsLoading = ref<Promise<void> | null>(null);
const jobDropdown = ref<InstanceType<typeof FileResizableDropdown> | null>(
  null,
);

const showJobIcon = computed(
  () =>
    props.file.JobBindings?.length > 0 &&
    (clientStore.client?.JobAccess !== 0 ||
      projectStore.project?.JobAccess !== 0),
);

const loadBoundJobs = async () => {
  if (boundJobs.value.length || boundJobsLoading.value) {
    return boundJobsLoading.value;
  }

  boundJobsLoading.value = Promise.all(
    props.file.JobBindings.map((binding) => JobsApi.getJobInfo(binding.JobId)),
  )
    .then((jobs) => {
      boundJobs.value = jobs;
    })
    .finally(() => {
      boundJobsLoading.value = null;
    });

  return boundJobsLoading.value;
};

const goToJob = (job: Job) => {
  resetJobNavigationState();
  navigateToJob(job, route, router);
};

const goToSingleJob = async () => {
  await loadBoundJobs();
  const job = boundJobs.value[0]?.Job;
  if (job) goToJob(job);
};

const fileItemRef = ref<HTMLElement | null>(null);
const isMultiline = ref(false);
let fileResizeObserver: ResizeObserver | null = null;
const emit = defineEmits(["select", "selectFileAction", "rename", "replace"]);

let isEditable = ref(false);
let isNameEditable = ref(false);
let levelStyle = computed(() => {
  return {
    paddingLeft: `${props.level == 0 ? 2 : props.level * 16}px`,
  };
});
const isFlashing = ref(false);

const menuElementsFile = computed(() => {
  if ((props.ruleAccess == 2 && props.file.Editable) || isAdmin) {
    return [
      {
        name: String(i18n.global.t("file_rename")),
        nameIcon: "edit-pencil",
        event: "renameFile",
      },
      { name: String(i18n.global.t("file_download")), nameIcon: "download", event: "downloadFile" },
      {
        name: String(i18n.global.t("file_view_information")),
        nameIcon: "info-circle-outline",
        event: "infoFile",
      },
      { name: String(i18n.global.t("remove")), nameIcon: "delete-outline", event: "removeFile" },
    ];
  } else if (props.ruleAccess !== 2 && props.file.Editable) {
    return [
      { name: String(i18n.global.t("file_download")), nameIcon: "download", event: "downloadFile" },
      {
        name: String(i18n.global.t("file_view_information")),
        nameIcon: "info-circle-outline",
        event: "infoFile",
      },
    ];
  }

  return [
    {
      name: String(i18n.global.t("file_view_information")),
      nameIcon: "info-circle-outline",
      event: "infoFile",
    },
  ];
});

const dragIconName = computed(() => {
  return (
    typeIconMap[getFileTypeFromString(props.file.Name)] || "file-universal"
  );
});

const searchMode = inject<Ref<boolean>>("searchMode");
const percentUploading = inject<boolean>("percentUploading");
const searchText = inject<Ref<string>>("searchText");
const isAdmin = inject("isAdmin");
const dirFocusId = inject<Ref<string>>("dirFocusId");
const renameFileFocus = inject<Ref<boolean>>("renameFileFocus", ref(false));
const disableRenameFileFocus = inject<Function>("disableRenameFileFocus");
const changeElFocusId = inject<Function>("changeElFocusId");
const flashFileId = inject<Ref<number | null>>("flashFileId", ref(null));
const dropdownMenu = ref<InstanceType<typeof FileDropdown> | null>(null);
const suppressNextFileClick = ref(false);

interface ChildEventData {
  type: string;
  data: any;
}

const emitToParent =
  inject<(eventData: ChildEventData) => boolean | Promise<boolean>>(
    "childEventEmitter",
  );

const containerProject = inject<Ref<ProjectContainer>>("containerProject");

const changeModalPos = inject<Function>("changeModalPos");

const menuAnchorRef = ref<HTMLElement | null>(null);

const setModalPositionNearDropdown = () => {
  const el = menuAnchorRef.value;
  if (!el) return;

  const rect = el.getBoundingClientRect();

  changeModalPos?.({
    x: rect.right + window.scrollX + 8,
    y: rect.top + window.scrollY,
  });
};

const onJobsClosed = () => {
  if (!fileItemRef.value?.matches(":hover")) {
    isEditable.value = false;
    dropdownMenu.value?.closeDropdown();
  }
};

const resetJobNavigationState = () => {
  jobDropdown.value?.close();
  dropdownMenu.value?.closeDropdown();
  isEditable.value = false;
  stopGlobalHoverWatch();
};

const isSelectionInsideInput = () => {
  const selection = window.getSelection();
  const inputEl = inputRef.value?.$el as HTMLElement | undefined;

  if (!selection || selection.isCollapsed || !inputEl) return false;

  const { anchorNode, focusNode } = selection;

  return (
    !!anchorNode &&
    !!focusNode &&
    inputEl.contains(anchorNode) &&
    inputEl.contains(focusNode)
  );
};

const handleMouseDownCapture = () => {
  suppressNextFileClick.value = isSelectionInsideInput();
};

const highlightedName = computed(() => {
  const escapedName = escapeHtml(props.file.Name);
  if (!searchMode?.value || !searchText?.value) return escapedName;

  return highlightFileSearchText(searchText.value, escapedName);
});

type FileTag = FileOut["Tags"][number];

const selectedTags = ref<TagWithFrequency[]>([]);
const isTagsUpdating = ref(false);

const mapFileTagToSelectorTag = (tag: FileTag): TagWithFrequency => ({
  ...tag,
  Frequency: 0,
  FullName: tag.Name,
  Type: TagBaseType.Common,
});

const mapSelectorTagToFileTag = (tag: TagWithFrequency): FileTag => ({
  Signed: false,
  SignedBy: "",
  SignedAt: null as unknown as Date,
  Id: tag.Id,
  Name: tag.Name,
});

const isSameTag = (
  a: { Id: any; Name: string },
  b: { Id: any; Name: string },
): boolean => {
  const aIdExists = a.Id !== null && a.Id !== undefined;
  const bIdExists = b.Id !== null && b.Id !== undefined;

  if (aIdExists && bIdExists) {
    return String(a.Id) === String(b.Id);
  }

  return a.Name.trim().toLowerCase() === b.Name.trim().toLowerCase();
};

watch(
  () => props.file.Tags,
  (tags) => {
    selectedTags.value = (tags ?? []).map(mapFileTagToSelectorTag);
  },
  { immediate: true, deep: true },
);

const changeTags = async (newSelectedTags: TagWithFrequency[]) => {
  if (isTagsUpdating.value) return;

  const currentFileTags: FileTag[] = props.file.Tags ?? [];
  const nextSelectedTags: TagWithFrequency[] = newSelectedTags ?? [];

  const addedTags = nextSelectedTags.filter(
    (selectedTag) =>
      !currentFileTags.some((fileTag) => isSameTag(fileTag, selectedTag)),
  );

  const removedTags = currentFileTags.filter(
    (fileTag) =>
      !nextSelectedTags.some((selectedTag) => isSameTag(fileTag, selectedTag)),
  );

  if (addedTags.length === 0 && removedTags.length === 0) {
    return;
  }

  isTagsUpdating.value = true;

  try {
    const success = await emitToParent?.({
      type: "changeFileTags",
      data: {
        dir: props.parentNode,
        file: props.file,
        tags: nextSelectedTags.map(mapSelectorTagToFileTag),
        addedTags,
        removedTags,
      },
    });

    if (!success) {
      selectedTags.value = currentFileTags.map(mapFileTagToSelectorTag);
    }
  } catch (error) {
    selectedTags.value = currentFileTags.map(mapFileTagToSelectorTag);
  } finally {
    isTagsUpdating.value = false;
  }
};

const onFileTagCreated = async (tags: FileTag[]) => {
  selectedTags.value = (tags ?? []).map(mapFileTagToSelectorTag);

  await emitToParent?.({
    type: "setFileTags",
    data: {
      file: props.file,
      tags,
    },
  });
};

const removeTag = async (item: { Id: any; Name: string }) => {
  if (isTagsUpdating.value) return;

  const nextSelectedTags = (selectedTags.value ?? []).filter(
    (tag) => !isSameTag(tag, item),
  );

  await changeTags(nextSelectedTags);
};

const abortUpload = () => {
  emitToParent?.({
    type: "abort",
    data: {
      file: props.file,
    },
  });
};

const advancedMode = inject("mode");

const inputRef = ref<InstanceType<typeof FileInput> | null>(null);

const selectFile = (value: boolean) => {
  emit("select", props.file, value);
};

const deselectFile = () => {
  emit("select", props.file, false);
};

const renameFile = (value: boolean) => {
  if (changeElFocusId) {
    changeElFocusId(props.file.Id);
  }

  emit("rename", props.file, value);
};

const handleFileItemClick = (event: MouseEvent) => {
  if (
    (containerProject?.value.FileAccess == 0 && !isAdmin) ||
    !props.file.Editable
  )
    return;
  if (advancedMode.value || props.isUploading == true) return;

  if (isNameEditable.value) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  if (!window.getSelection()?.isCollapsed) {
    return;
  }
  
  if (isMobile.value) return;

  setModalPositionNearDropdown();
  chooseAction("downloadFileClick");
};

const chooseAction = (option: string) => {
  if (option == "renameFile" && inputRef.value) {
    inputRef.value.focus();
    return;
  }

  if (option == "downloadFileClick") {
    emit("selectFileAction", props.file, "downloadFile");
    return;
  }

  emit("selectFileAction", props.file, option);
};

const editName = async () => {
  isNameEditable.value = true;

  await nextTick();

  inputRef.value?.focus();
};

const blurInput = () => {
  isNameEditable.value = false;
};

const finishEditingOnOutsideTap = (event: PointerEvent) => {
  if (!isMobile.value || !isNameEditable.value) return;
  if (fileItemRef.value?.contains(event.target as Node)) return;

  inputRef.value?.finishEditing();
};

watch(isNameEditable, (editing) => {
  if (editing) {
    document.addEventListener("pointerdown", finishEditingOnOutsideTap, true);
  } else {
    document.removeEventListener("pointerdown", finishEditingOnOutsideTap, true);
  }
});

let closeMenuTimer: ReturnType<typeof setTimeout> | null = null;

const startGlobalHoverWatch = () => {
  document.addEventListener("mousemove", checkHoverState);
};

const stopGlobalHoverWatch = () => {
  document.removeEventListener("mousemove", checkHoverState);
};

const checkHoverState = () => {
  const overFile = fileItemRef.value?.matches(":hover");
  const overDropdownContent = !!document.querySelector(
    ".dropdown-content:hover",
  );
  const overFileDropdown = !!dropdownMenu.value?.$el?.matches(":hover");

  if (!overFile && !overDropdownContent && !overFileDropdown) {
    isEditable.value = false;
    dropdownMenu.value?.closeDropdown();
    stopGlobalHoverWatch();
  }
};

const handleMouseEnter = () => {
  isEditable.value = true;
  startGlobalHoverWatch();
};

const handleMouseLeave = (event: MouseEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement | null;
  if (relatedTarget?.closest(".dropdown-content")) return;

  isEditable.value = false;
  dropdownMenu.value?.closeDropdown();
  stopGlobalHoverWatch();
};

onMounted(() => {
  fileResizeObserver = new ResizeObserver(([entry]) => {
    const content =
      entry.target.querySelector<HTMLElement>(".file-item__string");
    isMultiline.value = (content?.getBoundingClientRect().height ?? 0) > 20;
  });

  if (fileItemRef.value) {
    fileResizeObserver.observe(fileItemRef.value);
  }
});

onUnmounted(() => {
  if (closeMenuTimer) {
    clearTimeout(closeMenuTimer);
  }

  fileResizeObserver?.disconnect();
});

onUnmounted(() => {
  stopGlobalHoverWatch();
  document.removeEventListener("pointerdown", finishEditingOnOutsideTap, true);
});

watch(
  () => flashFileId?.value,
  async (newId) => {
    if (newId === props.file.Id) {
      await nextTick();

      if (fileItemRef.value) {
        fileItemRef.value.style.animation = "none";
        fileItemRef.value.offsetHeight;
        fileItemRef.value.style.animation = "";
      }

      isFlashing.value = true;

      await nextTick();
      fileItemRef.value?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      setTimeout(() => {
        isFlashing.value = false;
      }, 2000);
    }
  },
);

watch(
  () => renameFileFocus.value,
  (newValue) => {
    if (newValue === true && dirFocusId?.value == props.file.Id) {
      isNameEditable.value = true;

      nextTick(() => {
        inputRef.value?.focus();
      });

      disableRenameFileFocus?.();
    }
  },
);

const dragState = inject<{
  draggedEntity: DragEntity | null;
  dropTarget: FolderOut | null;
  isValidDrop: boolean;
}>("dragState", {
  draggedEntity: null,
  dropTarget: null,
  isValidDrop: false,
});
const setDragState = inject<Function>("setDragState");

const dragGeneralState = inject<DragGeneralState>("dragGeneralState", {
  name: "",
  type: "",
  count: 0,
  active: false,
  isValid: true,
  x: 0,
  y: 0,
});

let isDragStart = ref(false);
let isDragSource = ref(false);

type DragEntity =
  | { type: "folder"; folder: FolderOut }
  | { type: "file"; file: FileOut; parentFolder: FolderOut }
  | {
      type: "selection";
      folders: FolderOut[];
      files: { file: FileOut; parentFolder: FolderOut }[];
    };

const getSelectedDragEntity = inject<() => DragEntity>("getSelectedDragEntity");

const handleDragStart = (event: DragEvent) => {
  const sel = window.getSelection();
  if (sel && !sel.isCollapsed) {
    event.preventDefault();
    sel.removeAllRanges();
    return;
  }

  if (!advancedMode.value) {
    event.preventDefault();
    return;
  }

  if (!event.dataTransfer) return;

  const img = document.createElement("div");
  img.style.width = "0";
  img.style.height = "0";
  document.body.appendChild(img);
  event.dataTransfer.setDragImage(img, 0, 0);
  event.dataTransfer.effectAllowed = "copyMove";

  dragGeneralState.active = true;
  dragGeneralState.x = event.clientX;
  dragGeneralState.y = event.clientY;

  let draggedEntity: DragEntity = props.parentNode
    ? {
        type: "file",
        file: props.file,
        parentFolder: props.parentNode,
      }
    : {
        type: "selection",
        folders: [],
        files: [],
      };

  const selection = getSelectedDragEntity?.();
  if (selection && selection.type === "selection") {
    const hasAnySelection =
      selection.folders.length > 0 || selection.files.length > 0;

    if (hasAnySelection && props.file.isSelected === true) {
      draggedEntity = selection;
    }
  }

  const toPayload = (f: FileOut) => ({
    Id: f.Id,
    Name: f.Name,
    Signed: f.Signed,
  });
  if (draggedEntity.type === "file") {
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify([toPayload(props.file)]),
    );
  } else if (draggedEntity.type === "selection") {
    const files = draggedEntity.files.map((f) => toPayload(f.file));
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify(files.length ? files : [toPayload(props.file)]),
    );
  } else {
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        type: draggedEntity.type,
      }),
    );
  }

  setDragState?.({
    draggedEntity,
    draggedElement: event.target as HTMLElement,
    dropTarget: null,
    isValidDrop: false,
  });

  isDragSource.value = true;
};

function onDrag(e: DragEvent) {
  if (!e.clientX && !e.clientY) return;

  dragGeneralState.x = e.clientX;
  dragGeneralState.y = e.clientY;
}

const handleDragEnd = () => {
  setDragState?.({
    draggedEntity: null,
    draggedElement: null,
    dropTarget: null,
    isValidDrop: false,
  });

  dragGeneralState.active = false;
  isDragSource.value = false;
  isDragStart.value = false;
};

let isDragValid = ref(true);
const isExternalFileDrag = (event: DragEvent): boolean => {
  const types = Array.from(event.dataTransfer?.types ?? []);
  return types.includes("Files");
};

const handleDragOver = (event: DragEvent) => {
  const externalFileDrag = isExternalFileDrag(event);

  if (!externalFileDrag) {
    isDragValid.value = false;
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  if (props.ruleAccess !== 2 || !props.file.Editable) {
    isDragValid.value = false;
    event.dataTransfer!.dropEffect = "none";
    return;
  }

  isDragValid.value = true;

  const items = event.dataTransfer?.items ?? [];
  for (const item of items) {
    const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;

    if (entry && !entry.isFile) {
      isDragValid.value = false;
      break;
    }
  }

  event.dataTransfer!.dropEffect = isDragValid.value ? "move" : "none";
};

const handleDragEnter = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
  if (isDragSource.value) return;
  isDragStart.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  const current = event.currentTarget as HTMLElement;
  const related = event.relatedTarget as HTMLElement | null;

  if (related && current.contains(related)) return;

  isDragValid.value = true;
  isDragStart.value = false;
};

const handleDrop = async (event: DragEvent) => {
  const externalFileDrag = Array.from(event.dataTransfer?.types ?? []).includes(
    "Files",
  );

  if (!externalFileDrag || dragState.draggedEntity) {
    isDragValid.value = false;
    isDragStart.value = false;
    return;
  }

  if (props.ruleAccess !== 2 || !props.file.Editable) {
    isDragValid.value = false;
    isDragStart.value = false;
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) {
    isDragStart.value = false;
    return;
  }

  if (files && files.length > 0) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const maxSize = 100 * 1024 * 1024;
      if (file.size > maxSize) {
        if (emitToParent) {
          emitToParent({
            type: "error",
            data: {
              type: "error",
              errorType: "size",
            },
          });
        }

        isDragStart.value = false;

        return;
      }

      const fileExtension = getFileTypeFromString(file.name);
      const originalExtension = getFileTypeFromString(props.file.Name);

      if (fileExtension !== originalExtension) {
        if (emitToParent) {
          emitToParent({
            type: "error",
            data: {
              type: "error",
              errorType: "extension",
            },
          });
        }

        isDragStart.value = false;

        return;
      }

      if (file.type == "") {
        if (emitToParent) {
          emitToParent({
            type: "error",
            data: {
              type: "error",
              errorType: "folder",
            },
          });
        }

        return;
      }
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append("File", file);
      emit("replace", props.file, file.name, formData);
    }

    isDragStart.value = false;

    return;
  }

  isDragStart.value = false;
};
</script>

<style lang="scss" scoped>
@import "@/styles/color.scss";

@keyframes file-flash {
  0%,
  33.33%,
  66.66%,
  100% {
    background-color: transparent;
  }
  16.66%,
  50%,
  83.33% {
    background-color: #f3f8f4;
  }
}

.file-item {
  display: flex;
  width: 100%;
  min-height: 24px;
  border-radius: 8px;
  padding: 2px;
  gap: 4px;
  box-sizing: border-box;
  border: 1px solid transparent;
  cursor: pointer;
  align-items: flex-start;
  position: relative;

  &--multiline {
    min-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }

  &--flash {
    animation: file-flash 1.8s ease-in-out;
  }

  &-modal {
    margin-top: 0;
    padding: 0;
  }

  &-dragging-valid {
    z-index: 3;
    outline: 1px $color-on-accent dashed;

    .file-item__input {
      border: none !important;
    }
  }

  &-dragging-invalid {
    z-index: 3;
    outline: 1px $color-urgent dashed;

    .file-item__input {
      border: none !important;
    }

    :deep(.file-input__text-field) {
      color: #d2d2d2;
    }
  }

  &_editable {
    background-color: $color-divider;
  }

  &-download {
    cursor: pointer;
  }

  &__checkbox {
    padding-top: 1px;
    flex: 0 0 auto;
  }

  &__string {
    display: block;
    min-width: 0;
    flex: 0 1 auto;
    line-height: 18px;

    &-hashtag {
      display: inline-flex;
      vertical-align: top;
      white-space: nowrap;
      margin-right: 4px;
    }

    &-icon {
      margin-right: 2px;
      display: inline-flex;
      align-items: flex-start;
      justify-content: center;
      flex: 0 0 auto;
    }
  }

  &__name {
    display: inline;
    min-width: 0;
    color: $color-text_main;
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;
    word-break: break-word;

    &-disable {
      color: $color-text_unavailable;
    }
  }

  &__percent {
    display: inline;
    color: $color-text_main;
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;

    &-text {
      display: inline;
      color: $color-text_unavailable;
    }
  }

  &__option-icons {
    position: relative;
    display: flex;
    align-items: flex-start;
    flex: 0 0 auto;
    margin-left: 8px;

    &-icon {
      width: 16px;
      height: 16px;
      display: inline-flex;
      align-items: flex-start;
      justify-content: center;
      flex: 0 0 16px;

      &-invisible {
        opacity: 0;
        pointer-events: none;
      }

      &-modal {
        margin-left: auto;
      }
    }
  }

  &__jobs-list {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 236px;
    padding: 8px;
    overflow-y: auto;

    &-divider {
      flex-shrink: 0;
      height: 1px;
      margin: 4px 0;
      background-color: $color-divider;
    }
  }
}
</style>
